import type { SerializedTableState } from '@/components/shared/fancyTables';
import { useFancyTablesStore } from '@/stores/fancy-tables.store';
import { debounce, isEqual } from 'lodash';
import { onMounted, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApiUtils } from './api-utils.composable';

interface TableConfigState extends SerializedTableState {
  id?: number;
  name?: string;
  isDefault?: boolean;
}

interface UseFancyTableConfigOptions {
  tableName: string;
  fancyTableRef: Ref<any>;
  tableLoader: () => Promise<void>;
}

export function useFancyTableConfig(options: UseFancyTableConfigOptions) {
  const { tableName, fancyTableRef, tableLoader } = options;
  const isStateLoaded = ref(false);
  const { t } = useI18n();
  const {
    getSavedTableStates,
    saveTableState,
    updateTableState,
    deleteTableState,
    markTableStateAsDefault,
  } = useFancyTablesStore();
  const { handleApiError } = useApiUtils();

  // Reactive state
  const savedTableConfigs = ref<TableConfigState[]>([]);
  const currentSavedState = ref<TableConfigState | undefined>(undefined);
  const canSaveState = ref(false);

  /**
   * Loads saved table configurations from the server
   */
  const loadSavedConfig = async (): Promise<void> => {
    try {
      savedTableConfigs.value = await getSavedTableStates(tableName);

      if (savedTableConfigs.value.length === 0) {
        return;
      }

      // Try to find default config first, otherwise use the first one
      const defaultConfig = savedTableConfigs.value.find(config => config.isDefault);

      currentSavedState.value = defaultConfig || savedTableConfigs.value[0];
    } catch (error) {
      console.error('Failed to load saved table configurations:', error);
    } finally {
      isStateLoaded.value = true;
    }
  };

  /**
   * Handles table state changes and determines if save state should be enabled
   */
  const handleTableStateChange = (state: SerializedTableState): void => {
    if (!currentSavedState.value) {
      canSaveState.value = true;
      return;
    }

    const relevantKeys: (keyof SerializedTableState)[] = [
      'columnVisibility',
      'columnOrder',
      'columnSizing',
      'columnPinning',
      'columnFilters',
      'sorting',
      'pageSize',
    ];

    const hasAnyChanges = relevantKeys.some(key => {
      return !isEqual(state[key], currentSavedState.value?.[key]);
    });
    // log debug changed columns
    // Log a table of changed keys with previous and new values
    const changedKeys = relevantKeys.filter(
      key => !isEqual(state[key], currentSavedState.value?.[key])
    );
    if (changedKeys.length > 0) {
      console.table(
        changedKeys.map(key => ({
          key,
          previous: currentSavedState.value?.[key],
          current: state[key],
        }))
      );
    }

    canSaveState.value = hasAnyChanges;
  };

  const debouncedHandleTableStateChange = debounce(handleTableStateChange, 300);

  /**
   * Saves a new table configuration
   */
  const handleSaveConfig = async (configName: string, setAsDefault: boolean): Promise<void> => {
    if (!fancyTableRef.value) {
      throw new Error('Table reference is not available');
    }

    const payload = {
      ...fancyTableRef.value.getSerializedState(),
      name: configName,
      isDefault: setAsDefault,
    };

    try {
      const response = await handleApiError(
        () => saveTableState(tableName, payload),
        t('common.forms.changes_saved')
      );

      // Refresh saved configs and update current state
      savedTableConfigs.value = await getSavedTableStates(tableName);
      const currentConfigIndex = savedTableConfigs.value.findIndex(
        config => config.id === response.id
      );
      if (currentConfigIndex !== -1) {
        currentSavedState.value = savedTableConfigs.value[currentConfigIndex];
      } else {
        // find default config
        const defaultConfig = savedTableConfigs.value.find(config => config.isDefault);
        if (defaultConfig) {
          currentSavedState.value = defaultConfig;
        } else if (savedTableConfigs.value.length > 0) {
          currentSavedState.value = savedTableConfigs.value[0];
        }
        if (currentSavedState.value?.id) {
          await handleApplyConfig(currentSavedState.value.id!);
        }
      }

      canSaveState.value = false;
    } catch (error) {
      console.error('Failed to save table configuration:', error);
      throw error;
    }
  };

  /**
   * Applies an existing table configuration
   */
  const handleApplyConfig = async (configId: number): Promise<void> => {
    if (!fancyTableRef.value) {
      throw new Error('Table reference is not available');
    }

    try {
      const configToApply = savedTableConfigs.value.find(config => config.id === configId);

      if (!configToApply) {
        throw new Error(`Configuration with ID ${configId} not found`);
      }

      currentSavedState.value = configToApply;
      fancyTableRef.value.updateTableState(currentSavedState.value);
      canSaveState.value = false;
    } catch (error) {
      console.error('Failed to apply table configuration:', error);
      throw error;
    }
  };

  /**
   * Updates an existing table configuration
   */
  const handleUpdateConfig = async (configId: number): Promise<void> => {
    if (!fancyTableRef.value) {
      throw new Error('Table reference is not available');
    }

    const payload = {
      ...fancyTableRef.value.getSerializedState(),
    };

    try {
      await handleApiError(
        () => updateTableState(configId, payload),
        t('common.forms.changes_saved')
      );

      // Refresh saved configs and update current state
      savedTableConfigs.value = await getSavedTableStates(tableName);
      currentSavedState.value = savedTableConfigs.value.find(config => config.id === configId);
      canSaveState.value = false;
    } catch (error) {
      console.error('Failed to update table configuration:', error);
      throw error;
    }
  };

  /**
   * Refreshes the saved table configurations from the server
   */
  const refreshSavedConfigs = async (): Promise<void> => {
    try {
      savedTableConfigs.value = await getSavedTableStates(tableName);
    } catch (error) {
      console.error('Failed to refresh saved table configurations:', error);
      throw error;
    }
  };

  const handleDeleteConfig = async (configId: number): Promise<void> => {
    try {
      const wasCurrentConfig = currentSavedState.value?.id === configId;

      await handleApiError(() => deleteTableState(configId), t('common.forms.changes_saved'));

      // Refresh the configs list
      await refreshSavedConfigs();

      // If we deleted the currently selected config, select a new one
      if (wasCurrentConfig && savedTableConfigs.value.length > 0) {
        // Try to find default config first, otherwise use the first one
        const defaultConfig = savedTableConfigs.value.find(config => config.isDefault);
        const newConfig = defaultConfig || savedTableConfigs.value[0];
        currentSavedState.value = newConfig;

        // Apply the new config to the table
        if (newConfig.id) {
          await handleApplyConfig(newConfig.id);
        }
      } else if (savedTableConfigs.value.length === 0) {
        // No configs left, clear current state
        currentSavedState.value = undefined;
        canSaveState.value = true;
      }
    } catch (error) {
      console.error('Failed to delete table configuration:', error);
      throw error;
    }
  };

  const handleMarkAsDefault = async (configId: number): Promise<void> => {
    try {
      await handleApiError(
        () => markTableStateAsDefault(configId),
        t('common.forms.changes_saved')
      );
      refreshSavedConfigs();
    } catch (error) {
      console.error('Failed to mark table configuration as default:', error);
      throw error;
    }
  };

  onMounted(async () => {
    await loadSavedConfig();
    await tableLoader();
  });

  return {
    // Reactive state
    savedTableConfigs,
    currentSavedState,
    canSaveState,
    isStateLoaded,

    // Methods
    loadSavedConfig,
    handleTableStateChange,
    debouncedHandleTableStateChange,
    handleSaveConfig,
    handleApplyConfig,
    handleUpdateConfig,
    refreshSavedConfigs,
    handleDeleteConfig,
    handleMarkAsDefault,
  };
}
